// useExportFormats — the export dialog's offered format list, gated to the
// codec-backed targets for the active material's template (REQ-FORMATS-001).
// Extracted from useAppShellHost (C1 #9). A hook because the labels/hints use
// the lingui `t` macro; the gating is the pure core query `packageableFormats`.

import * as DemoScope from "@bilda/app/demoScope";
import * as Core from "@bilda/core";
import { useLingui } from "@lingui/react/macro";
import type { ExportFormat } from "../../components/ExportFlow/ExportFlow.js";

type ActiveTemplate = {
    readonly id: string;
    readonly version: number;
} | null;

export const useExportFormats = (
    activeMaterialTemplate: ActiveTemplate,
): readonly ExportFormat[] => {
    const { t } = useLingui();
    // The full catalogue the dialog COULD show, narrowed to the
    // demonstrator's visible formats (DemoScope, FR-10.4: Torus and Canvas; the
    // other entries stay so widening that list brings them back), then
    // gated below.
    const catalogue: readonly ExportFormat[] = [
        {
            id: "F_html",
            label: t`Web page`,
            hint: t`A self-contained file you can open or print anywhere`,
        },
        { id: "F_pdf", label: t`PDF`, hint: t`A print-ready document` },
        {
            id: "F_torus",
            label: t`Torus package`,
            hint: t`For the Torus / OLI platform`,
        },
        {
            id: "F_canvas",
            label: t`Common Cartridge`,
            hint: t`Imports into Canvas and most LMSs`,
        },
        { id: "F_markdown", label: t`Markdown`, hint: t`Plain portable text` },
    ];
    const allExportFormats = catalogue.filter((format) =>
        DemoScope.isVisibleExportFormat(format.id),
    );
    // Gate to the codec-backed targets for THIS material's template: a format
    // with no renderer/codec path is shown disabled-with-reason so the author
    // can't pick one that dead-ends in a `failed` export. When no template is
    // known (no material focused / legacy wire), offer everything.
    const packageableFormatIds: ReadonlySet<string> | null =
        activeMaterialTemplate === null
            ? null
            : new Set(
                  Core.Packaging.State.packageableFormats(
                      Core.Packaging.State.initial,
                      Core.Template.Ref.mk(
                          Core.Template.Id.mk(activeMaterialTemplate.id),
                          Core.Versioning.Version.mk(
                              activeMaterialTemplate.version,
                          ),
                      ),
                  ).map((ref) => Core.Format.Id.from(ref.id)),
              );
    return packageableFormatIds === null
        ? allExportFormats
        : allExportFormats.map((format) =>
              packageableFormatIds.has(format.id)
                  ? format
                  : {
                        ...format,
                        disabledReason: t`Not available for this material`,
                    },
          );
};
